import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import Button from '../../components/common/Button';
import ConfirmDeleteModal from '../../components/common/ConfirmDeleteModal';
import { judgesApi, extractOne } from '../../api/facades';
import { judgeAvatarInitials } from './judgeHelpers';

const JUDGES_BASE = '/dashboard/settings/judges';

const JudgeDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const numId = id ? parseInt(id, 10) : NaN;
  const [showDelete, setShowDelete] = React.useState(false);

  const { data: res, isLoading, isError } = useQuery({
    queryKey: ['judges', numId],
    queryFn: () => judgesApi.get(numId),
    enabled: !Number.isNaN(numId),
  });
  const row = extractOne(res);

  const deleteMu = useMutation({
    mutationFn: () => judgesApi.remove(numId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['judges'] });
      setShowDelete(false);
      navigate(JUDGES_BASE);
      toast.success('Judge deleted.');
    },
    onError: (e: any) => toast.error(e?.message || 'Delete failed.'),
  });

  if (Number.isNaN(numId) || isError || (!isLoading && !row)) {
    return (
      <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
        <h1 className="text-2xl font-bold text-slate-700 dark:text-slate-200 mb-2">Judge not found</h1>
        <Button variant="secondary" onClick={() => navigate(JUDGES_BASE)}>Back to Judges</Button>
      </div>
    );
  }

  const r = row as any;
  const judgeName = r?.judge_name ?? 'Judge';
  const initials = judgeAvatarInitials(judgeName);
  const isActive = r?.is_active === 'Yes';

  return (
    <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-emerald-700 text-lg font-semibold text-white">
            {initials}
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-700 dark:text-slate-200">{judgeName}</h1>
            <p className="text-slate-600 dark:text-slate-400 mt-0.5 text-sm">Judge</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => navigate(JUDGES_BASE)}>BACK</Button>
          <Button variant="secondary" onClick={() => navigate(`${JUDGES_BASE}/${id}/edit`)}>Edit</Button>
          <Button variant="outline" onClick={() => setShowDelete(true)}>Delete</Button>
        </div>
      </div>

      <div className="mt-8 border-t border-gray-100 dark:border-gray-700 pt-6">
        <h2 className="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide mb-3">Details</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="text-xs text-slate-500 dark:text-slate-400">Active</div>
            <div className="text-slate-700 dark:text-slate-200">{isActive ? 'Yes' : 'No'}</div>
          </div>
        </div>
      </div>

      <ConfirmDeleteModal open={showDelete} onClose={() => setShowDelete(false)} onConfirm={() => deleteMu.mutate()} title="Delete judge" message="Are you sure? This cannot be undone." loading={deleteMu.isPending} />
    </div>
  );
};

export default JudgeDetailPage;
